import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonCard } from '@/components/ui/SkeletonLoader';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { estagioService } from '@/services/api';
import { Estagio, EstagioFilters } from '@/types';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

export const EstagiosPage: React.FC = () => {
  const [filters, setFilters] = useState<EstagioFilters>({
    page: 1,
    limit: 10,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchTerm || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateEstagios } = useQueryInvalidation();

  const { data: estagiosData, isLoading, error } = useQuery({
    queryKey: ['estagios', filters],
    queryFn: () => estagioService.list(filters),
  });

  const deleteEstagioMutation = useMutation({
    mutationFn: estagioService.delete,
    onSuccess: () => {
      invalidateEstagios();
      handleSuccess('Estágio excluído com sucesso!');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao excluir estágio');
    },
  });

  // Busca via debounce já ocorre no useEffect acima

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDelete = async (id: number) => {
    showConfirm(
      'Excluir Estágio',
      'Tem certeza que deseja excluir este estágio? Esta ação não pode ser desfeita.',
      async () => {
        await deleteEstagioMutation.mutateAsync(id);
      },
      'danger'
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ativo':
        return 'bg-green-100 text-green-800';
      case 'Cancelado':
        return 'bg-red-100 text-red-800';
      case 'Concluido':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Erro ao carregar estágios</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Estágios</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie os estágios cadastrados no sistema
          </p>
        </div>
        <Link to="/estagios/novo">
          <Button>
            <PlusIcon className="h-4 w-4 mr-2" />
            Novo Estágio
          </Button>
        </Link>
      </div>

      {/* Busca */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Filtros</CardTitle>
              <CardDescription>Busque e filtre os estágios cadastrados</CardDescription>
            </div>
            <div className="w-full md:w-72 relative">
              <input
                type="text"
                placeholder="Buscar por aluno, empresa ou instituição"
                className="w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  aria-label="Limpar busca"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchTerm("")}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Estagios List */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Estágios</CardTitle>
          <CardDescription>
            {estagiosData?.pagination.total || 0} estágios encontrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonCard count={5} />
          ) : estagiosData?.data.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhum estágio encontrado</p>
              <Link to="/estagios/novo">
                <Button className="mt-4">Cadastrar primeiro estágio</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {estagiosData?.data.map((estagio: Estagio) => (
                <div
                  key={estagio.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {estagio.aluno?.nome || 'Aluno não encontrado'}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(estagio.status)}`}>
                          {estagio.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Empresa:</span> {estagio.empresa?.nome || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Instituição:</span> {estagio.instituicao?.nome || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Tipo:</span> {estagio.tipo}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Início:</span> {new Date(estagio.dataInicio).toLocaleDateString('pt-BR')}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Término:</span> {new Date(estagio.dataTermino).toLocaleDateString('pt-BR')}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Remunerado:</span> {estagio.remunerado ? 'Sim' : 'Não'}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Link to={`/estagios/${estagio.id}`}>
                        <Button variant="outline" size="sm">
                          Ver
                        </Button>
                      </Link>
                      <Link to={`/estagios/${estagio.id}/editar`}>
                        <Button variant="outline" size="sm">
                          Editar
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDelete(estagio.id)}
                        disabled={deleteEstagioMutation.isPending}
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                      >
                        Excluir
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {estagiosData && estagiosData.pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={!estagiosData.pagination.hasPrev}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 text-sm text-gray-700">
                  Página {estagiosData.pagination.page} de {estagiosData.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={!estagiosData.pagination.hasNext}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={deleteEstagioMutation.isPending}
      />
    </div>
  );
};
